/* ==========================================================================
   Bundle inline reviews — <bundle-reviews-tabs>
   --------------------------------------------------------------------------
   Ticket goal: let users read each flavor's reviews WITHOUT leaving the Build
   a Bundle, so their in-progress selection (BundleSection._selected, kept only
   in memory) is never lost.

   How it works:
   - Each flavor card's star rating is a [data-bundle-reviews-trigger] button.
     Clicking it activates that flavor's tab here and scrolls to this section —
     no navigation, so nothing in the builder is reset.
   - Each tab maps to a panel holding a class-based Judge.me widget mount
     (.jdgm-review-widget, data-auto-install="false"). The active panel's widget
     is rendered client-side ON DEMAND via window.jdgm, then cached, so nothing
     renders on page load and re-visiting a tab is instant.

   This component never touches BundleSection / _selected — it is additive UI.
   ========================================================================== */
(function () {
  'use strict';

  if (window.customElements && customElements.get('bundle-reviews-tabs')) {
    return;
  }

  const TAB_SELECTOR = '[data-bundle-reviews-tab]';
  const PANEL_SELECTOR = '[data-bundle-reviews-panel]';
  const TRIGGER_SELECTOR = '[data-bundle-reviews-trigger]';
  const WIDGET_SELECTOR = '.jdgm-review-widget';
  // Markers Judge.me injects once a product review widget has actually rendered.
  const RENDERED_SELECTOR =
    '.jdgm-rev-widg__summary, .jdgm-rev-widg__reviews, .jdgm-rev-widg__header, .jdgm-paginate, .jdgm-rev';
  const NAV_KEYS = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];

  const prefersReducedMotion = () =>
    Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  class BundleReviewsTabs extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.tablist = this.querySelector('[role="tablist"]');
      this.tabs = Array.from(this.querySelectorAll(TAB_SELECTOR));
      this.panels = Array.from(this.querySelectorAll(PANEL_SELECTOR));
      this._rendered = new Set(); // productIds whose render was attempted/cached

      // Tab activation (clicks land inside this component).
      this._onClick = this._onClick.bind(this);
      this.addEventListener('click', this._onClick);

      // Keyboard navigation across the tablist (ARIA tabs pattern).
      if (this.tablist) {
        this._onKeydown = this._onKeydown.bind(this);
        this.tablist.addEventListener('keydown', this._onKeydown);
      }

      // The card star-rating triggers live in the flavor grid — OUTSIDE this
      // component but inside the same <bundle-section>. Delegate from there.
      this._scope = this.closest('bundle-section') || document;
      this._onTriggerClick = this._onTriggerClick.bind(this);
      this._scope.addEventListener('click', this._onTriggerClick);

      // Lazily render the default tab only once the section nears the viewport.
      this._observeInitialRender();
    }

    disconnectedCallback() {
      if (this._scope) this._scope.removeEventListener('click', this._onTriggerClick);
      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }
    }

    _onTriggerClick(event) {
      const trigger = event.target.closest && event.target.closest(TRIGGER_SELECTOR);
      if (!trigger) return;
      // Only handle triggers belonging to our own section.
      if (this._scope !== document && !this._scope.contains(trigger)) return;
      const productId = trigger.getAttribute('data-product-id');
      if (!this._panelFor(productId)) return;
      event.preventDefault();
      this.activate(productId, { scroll: true, focusTab: true });
    }

    _onClick(event) {
      const tab = event.target.closest && event.target.closest(TAB_SELECTOR);
      if (!tab || !this.contains(tab)) return;
      this.activate(tab.getAttribute('data-product-id'), { focusTab: true });
    }

    _onKeydown(event) {
      if (!NAV_KEYS.includes(event.key)) return;

      let current = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      if (current === -1) current = 0;

      let next = current;
      if (event.key === 'ArrowLeft') next = (current - 1 + this.tabs.length) % this.tabs.length;
      else if (event.key === 'ArrowRight') next = (current + 1) % this.tabs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.tabs.length - 1;

      if (next === current) return;
      event.preventDefault();
      this.activate(this.tabs[next].getAttribute('data-product-id'), { focusTab: true });
    }

    _panelFor(productId) {
      if (productId == null) return null;
      const id = String(productId);
      return this.panels.find((p) => p.getAttribute('data-product-id') === id) || null;
    }

    activate(productId, opts = {}) {
      const matchedPanel = this._panelFor(productId);
      if (!matchedPanel) return; // unknown flavor — leave current state untouched

      const id = String(productId);
      this.tabs.forEach((tab) => {
        const selected = tab.getAttribute('data-product-id') === id;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
        if (selected) {
          // Keep the active tab visible within the horizontal (scrollable) tablist.
          this._scrollTabIntoView(tab);
          if (opts.focusTab) {
            // preventScroll so the explicit smooth scroll below isn't fought by focus.
            try {
              tab.focus({ preventScroll: true });
            } catch (e) {
              tab.focus();
            }
          }
        }
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel !== matchedPanel;
      });

      this._renderPanel(matchedPanel);

      if (opts.scroll) this._scrollIntoView();
    }

    _scrollIntoView() {
      try {
        this.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
      } catch (e) {
        this.scrollIntoView();
      }
    }

    // Horizontally scroll the tablist so the active tab is fully visible, without
    // affecting page vertical scroll (avoids scrollIntoView's vertical side effects).
    _scrollTabIntoView(tab) {
      const tl = this.tablist;
      if (!tl) return;
      const pad = 16;
      const tabLeft = tab.offsetLeft;
      const tabRight = tabLeft + tab.offsetWidth;
      if (tabLeft < tl.scrollLeft) {
        tl.scrollLeft = Math.max(0, tabLeft - pad);
      } else if (tabRight > tl.scrollLeft + tl.clientWidth) {
        tl.scrollLeft = tabRight - tl.clientWidth + pad;
      }
    }

    _observeInitialRender() {
      const active = this.panels.find((p) => !p.hidden);
      if (!active) return;

      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              this._renderPanel(active);
              if (this._io) {
                this._io.disconnect();
                this._io = null;
              }
            }
          },
          { rootMargin: '200px 0px' }
        );
        this._io.observe(this);
      } else {
        this._renderPanel(active);
      }
    }

    _renderPanel(panel) {
      const productId = panel.getAttribute('data-product-id');
      if (this._rendered.has(productId)) return;
      this._rendered.add(productId);

      const count = parseInt(panel.getAttribute('data-review-count') || '0', 10);
      if (!count) return; // zero-review flavors keep their server-rendered empty state

      const widget = panel.querySelector(WIDGET_SELECTOR);
      if (!widget) return;

      // Judge.me's pre-rendered widget HTML (the judgeme.widget metafield) ships in
      // the DOM and is hydrated on page load — nothing to render, and we must NOT
      // disturb these nodes (jdgm has bound pagination/sort/form handlers to them).
      if (this._isRendered(panel)) return;

      this._renderWidget(panel);
    }

    _isRendered(panel) {
      const widget = panel.querySelector(WIDGET_SELECTOR);
      if (!widget) return false;
      if (widget.querySelector(RENDERED_SELECTOR)) return true;
      // Progressive enhancement: a populated judgeme.widget metafield ships the
      // reviews HTML server-side, so substantive children mean it's already visible.
      return widget.children.length > 0 && widget.textContent.trim().length > 0;
    }

    _renderWidget(panel) {
      if (!this._isRendered(panel)) this._setLoading(panel, true);

      const fire = () => {
        this._triggerJudgeMe(panel);
        this._verifyRender(panel);
      };

      if (window.jdgm) {
        fire();
        return;
      }

      // Judge.me's embed can mount after initial load — poll briefly for it
      // (mirrors the timing in judgeme-summary-truncate.js).
      let attempts = 0;
      const maxAttempts = 25;
      const timer = window.setInterval(() => {
        attempts += 1;
        if (window.jdgm) {
          window.clearInterval(timer);
          fire();
        } else if (attempts >= maxAttempts) {
          window.clearInterval(timer);
          this._verifyRender(panel);
        }
      }, 200);
    }

    _triggerJudgeMe(panel) {
      const widget = panel.querySelector(WIDGET_SELECTOR);
      if (!widget) return;

      // Only reached when a flavor's widget was NOT pre-rendered server-side.
      // Permit rendering now (suppressed on page load via data-auto-install) and
      // ask Judge.me to set up any widget still needing it. Non-destructive: we
      // never clone/replace the node (that would drop jdgm's bound handlers).
      widget.removeAttribute('data-auto-install');

      const jdgm = window.jdgm;
      if (jdgm && typeof jdgm === 'object') {
        // Judge.me v3 uses initializeWidgets(); older builds used other names.
        // All are idempotent for widgets already marked jdgm--done-setup.
        ['initializeWidgets', 'batchSetup', 'setupWidgets', 'docReady'].forEach((name) => {
          if (typeof jdgm[name] === 'function') {
            try {
              jdgm[name].call(jdgm);
            } catch (e) {
              /* try the next entry point */
            }
          }
        });
      }
    }

    _verifyRender(panel) {
      let checks = 0;
      const maxChecks = 12; // ~3.9s total
      const tick = () => {
        checks += 1;
        if (this._isRendered(panel)) {
          this._setLoading(panel, false);
          return;
        }
        if (checks >= maxChecks) {
          this._setLoading(panel, false);
          this._showFallback(panel);
          return;
        }
        window.setTimeout(tick, 300);
      };
      window.setTimeout(tick, 300);
    }

    _setLoading(panel, on) {
      let el = panel.querySelector('[data-bundle-reviews-loading]');
      if (on) {
        if (!el) {
          el = document.createElement('p');
          el.className = 'bundle-reviews__loading';
          el.setAttribute('data-bundle-reviews-loading', '');
          el.textContent = 'Loading reviews…';
          panel.insertBefore(el, panel.firstChild);
        }
      } else if (el) {
        el.parentNode.removeChild(el);
      }
    }

    _showFallback(panel) {
      if (panel.querySelector('[data-bundle-reviews-fallback]')) return;

      const url = panel.getAttribute('data-product-url');
      const title = panel.getAttribute('data-product-title') || 'this flavor';
      const p = document.createElement('p');
      p.className = 'bundle-reviews__fallback';
      p.setAttribute('data-bundle-reviews-fallback', '');

      if (url) {
        const a = document.createElement('a');
        a.href = url + (url.indexOf('?') >= 0 ? '&' : '?') + 'scroll_to_reviews=1#judgeme_product_reviews';
        // Open in a new tab so the builder (and the user's selection) stays put.
        a.target = '_blank';
        a.rel = 'noopener';
        a.className = 'bundle-reviews__fallback-link';
        a.textContent = 'Read all reviews for ' + title;
        p.appendChild(a);
      } else {
        p.textContent = 'Reviews are temporarily unavailable.';
      }
      panel.appendChild(p);
    }
  }

  customElements.define('bundle-reviews-tabs', BundleReviewsTabs);
})();
